import { useEffect, useState } from "react";
import { ArrowUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { scrollToTop } from "@/lib/scrollToTop";

/**
 * Return-to-top control for long pages (Anthony's ask, BEN-141 2i — there was no
 * such element in the DOM on any page).
 *
 * Mounted once per layout shell rather than per page: both shells scroll the
 * WINDOW (`AppLayout`'s `<main>` and `MarketingLayout`'s are in normal flow, with
 * no inner scroll container), so one window-scoped listener serves every route
 * under them and no page has to remember to opt in.
 *
 * Behaviour:
 *  - Hidden until there is something to return from — one viewport of scroll.
 *  - Scrolling is delegated to `lib/scrollToTop`, which requests `smooth`, honours
 *    `prefers-reduced-motion`, and falls back to an instant scroll where smooth is
 *    disabled — without that fallback this button was silently dead for anyone who
 *    turns smooth scrolling off (BEN-141 amendment 1).
 *  - `passive` listener, and state only flips on the threshold crossing, so this
 *    does not re-render on every scroll event.
 */

/** Show the button after one viewport of scrolling — below that, "top" is visible. */
const SHOW_AFTER_PX = 600;

export default function BackToTop() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > SHOW_AFTER_PX);
    onScroll(); // a deep-linked or restored scroll position must show it immediately
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <button
      type="button"
      onClick={scrollToTop}
      aria-label="Back to top"
      // Kept in the DOM and hidden with opacity/pointer-events rather than
      // unmounted, so the fade has something to animate and assistive tech is not
      // handed an element that appears and vanishes mid-scroll. `aria-hidden`
      // plus `pointer-events-none` keep it out of reach while invisible.
      aria-hidden={!visible}
      data-fab-dock="back-to-top"
      tabIndex={visible ? 0 : -1}
      className={cn(
        "fixed right-[calc(1.5rem+env(safe-area-inset-right))] bottom-[calc(1.5rem+var(--tabbar-space))] z-40 flex size-[40px] items-center justify-center rounded-full lg:size-11",
        "border border-border bg-card text-foreground shadow-lg",
        "transition-opacity duration-200 hover:bg-accent",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        visible ? "opacity-100" : "pointer-events-none opacity-0",
      )}
    >
      <ArrowUp className="size-[20px] lg:size-5" />
    </button>
  );
}
